import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "../../components/global/layout";
import ApiClient from "../../methods/api/apiClient";
import loader from "../../methods/loader";
import { toast } from "react-toastify";
import FormControl from "../../components/common/FormControl";
import {
  MODULE_CONFIG,
  MODULE_PERMISSIONS_SCHEMA,
  DEFAULT_STAFF_PERMISSIONS,
  countPermissions,
} from "./shared";
import {
  FiArrowLeft,
  FiSave,
  FiLock,
  FiUser,
  FiMail,
  FiRefreshCw,
  FiCheck,
  FiCheckSquare,
  FiSquare,
  FiShield,
  FiLayers,
} from "react-icons/fi";
import { HiOutlineUserGroup } from "react-icons/hi2";

export default function StaffAddEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  // Form State
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    permissions: JSON.parse(JSON.stringify(DEFAULT_STAFF_PERMISSIONS)),
  });

  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [passwordCopied, setPasswordCopied] = useState(false);

  // Password Generator
  const generateSecurePassword = () => {
    const uppercase = "ABCDEFGHJKLMNPQRSTUVWXYZ";
    const lowercase = "abcdefghijkmnopqrstuvwxyz";
    const numbers = "23456789";
    const symbols = "@$!%*?&#";
    const allChars = uppercase + lowercase + numbers + symbols;

    let pwd = "";
    pwd += uppercase.charAt(Math.floor(Math.random() * uppercase.length));
    pwd += lowercase.charAt(Math.floor(Math.random() * lowercase.length));
    pwd += numbers.charAt(Math.floor(Math.random() * numbers.length));
    pwd += symbols.charAt(Math.floor(Math.random() * symbols.length));

    for (let i = 4; i < 14; i++) {
      pwd += allChars.charAt(Math.floor(Math.random() * allChars.length));
    }
    const shuffled = pwd.split("").sort(() => 0.5 - Math.random()).join("");

    setFormData((prev) => ({ ...prev, password: shuffled }));

    if (navigator.clipboard) {
      navigator.clipboard.writeText(shuffled);
      setPasswordCopied(true);
      toast.info("Password generated and copied to clipboard!");
      setTimeout(() => setPasswordCopied(false), 3000);
    }
  };

  // Fetch Existing Staff Details for Edit Mode
  const fetchStaffDetail = useCallback(async () => {
    if (!id) return;
    loader(true);
    setLoading(true);

    try {
      const res = await ApiClient.get(`${MODULE_CONFIG.endpoints.detail}?id=${id}`);
      if (res?.success) {
        const staff = res?.data || res?.staff || res;

        // Merge incoming permissions with schema defaults to guarantee all keys exist
        const mergedPermissions = JSON.parse(JSON.stringify(DEFAULT_STAFF_PERMISSIONS));
        if (staff?.permissions && typeof staff.permissions === "object") {
          MODULE_PERMISSIONS_SCHEMA.forEach((mod) => {
            const incomingMod = staff.permissions[mod.key];
            if (incomingMod && typeof incomingMod === "object") {
              mod.actions.forEach((act) => {
                if (act.key in incomingMod) {
                  mergedPermissions[mod.key][act.key] = Boolean(incomingMod[act.key]);
                }
              });
            }
          });
        }

        setFormData({
          firstName: staff?.firstName || "",
          lastName: staff?.lastName || "",
          email: staff?.email || "",
          password: "", // Leave blank on edit unless deliberately changed
          permissions: mergedPermissions,
        });
      } else {
        toast.error("Failed to load staff details.");
        navigate(MODULE_CONFIG.routeBase);
      }
    } catch (err) {
      console.error("Error fetching staff detail:", err);
      toast.error("Error loading staff record.");
      navigate(MODULE_CONFIG.routeBase);
    } finally {
      setLoading(false);
      loader(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    if (isEdit) {
      fetchStaffDetail();
    }
  }, [isEdit, fetchStaffDetail]);

  // Handle Text Input Change
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Handle Granular Permission Toggle
  const handleToggleAction = (moduleKey, actionKey) => {
    setFormData((prev) => {
      const currentVal = Boolean(prev.permissions?.[moduleKey]?.[actionKey]);
      const nextVal = !currentVal;

      const updatedModule = {
        ...(prev.permissions?.[moduleKey] || {}),
        [actionKey]: nextVal,
      };

      // If user enabled any mutation action (create, edit, delete, reply, status_change, export),
      // ensure 'view' is also enabled so they can access the module!
      if (nextVal && actionKey !== "view") {
        updatedModule.view = true;
      }

      // If user disabled 'view', automatically disable all actions in that module
      if (!nextVal && actionKey === "view") {
        Object.keys(updatedModule).forEach((k) => {
          updatedModule[k] = false;
        });
      }

      return {
        ...prev,
        permissions: {
          ...prev.permissions,
          [moduleKey]: updatedModule,
        },
      };
    });
  };

  // Toggle All Actions for a Specific Module
  const handleToggleEntireModule = (moduleKey, actions) => {
    setFormData((prev) => {
      const currentModule = prev.permissions?.[moduleKey] || {};
      const allActive = actions.every((act) => Boolean(currentModule[act.key]));
      const nextState = !allActive;

      const updatedModule = {};
      actions.forEach((act) => {
        updatedModule[act.key] = nextState;
      });

      return {
        ...prev,
        permissions: {
          ...prev.permissions,
          [moduleKey]: updatedModule,
        },
      };
    });
  };

  // Quick Preset 1: Grant All Permissions
  const handleGrantAll = () => {
    const fullPermissions = {};
    MODULE_PERMISSIONS_SCHEMA.forEach((mod) => {
      fullPermissions[mod.key] = {};
      mod.actions.forEach((act) => {
        fullPermissions[mod.key][act.key] = true;
      });
    });
    setFormData((prev) => ({ ...prev, permissions: fullPermissions }));
    toast.success("All module permissions enabled.");
  };

  // Quick Preset 2: View Only Preset
  const handleViewOnlyPreset = () => {
    const viewOnly = {};
    MODULE_PERMISSIONS_SCHEMA.forEach((mod) => {
      viewOnly[mod.key] = {};
      mod.actions.forEach((act) => {
        viewOnly[mod.key][act.key] = act.key === "view";
      });
    });
    setFormData((prev) => ({ ...prev, permissions: viewOnly }));
    toast.info("Preset applied: Read-only access across all modules.");
  };

  // Quick Preset 3: Revoke All Permissions
  const handleRevokeAll = () => {
    const emptyPermissions = {};
    MODULE_PERMISSIONS_SCHEMA.forEach((mod) => {
      emptyPermissions[mod.key] = {};
      mod.actions.forEach((act) => {
        emptyPermissions[mod.key][act.key] = false;
      });
    });
    setFormData((prev) => ({ ...prev, permissions: emptyPermissions }));
    toast.warning("All permissions revoked.");
  };

  // Submit Handler
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.firstName.trim()) {
      toast.error("Please enter the staff member's First Name.");
      return;
    }
    if (!formData.lastName.trim()) {
      toast.error("Please enter the staff member's Last Name.");
      return;
    }
    if (!formData.email.trim()) {
      toast.error("Please enter a valid Email Address.");
      return;
    }

    if (!isEdit && !formData.password.trim()) {
      toast.error("Please provide an Admin Login Password for this sub administrator account.");
      return;
    }

    setSubmitting(true);
    loader(true);

    const payload = {
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      email: formData.email.trim(),
      permissions: formData.permissions,
    };

    if (!isEdit && formData.password.trim()) {
      payload.password = formData.password.trim();
    }

    if (isEdit) {
      payload.id = id;
    }

    try {
      const endpoint = isEdit ? MODULE_CONFIG.endpoints.update : MODULE_CONFIG.endpoints.create;
      const res = isEdit
        ? await ApiClient.put(endpoint, payload)
        : await ApiClient.post(endpoint, payload);

      if (res?.success) {
        toast.success(
          isEdit ? "Sub administrator updated successfully." : "Sub administrator created successfully."
        );
        navigate(MODULE_CONFIG.routeBase);
      } else {
        toast.error(res?.message || res?.error?.message || "Failed to save sub administrator record.");
      }
    } catch (err) {
      console.error("Save staff error:", err);
      toast.error("Failed to save sub administrator. Please check form data.");
    } finally {
      setSubmitting(false);
      loader(false);
    }
  };

  const { granted, total } = countPermissions(formData.permissions);

  return (
    <Layout>
      <div className="w-full max-w-full overflow-x-hidden space-y-5 sm:space-y-6 pb-16 antialiased">
        {/* ======================================================== */}
        {/* 1. TOP HEADER & ACTIONS                                  */}
        {/* ======================================================== */}
        <div className="bg-white rounded-2xl p-4 sm:p-5 border border-slate-300 shadow-[0_2px_10px_rgba(0,0,0,0.04)] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              onClick={() => navigate(MODULE_CONFIG.routeBase)}
              className="p-2.5 rounded-xl bg-slate-200 hover:bg-slate-300 text-slate-900 border border-slate-300 transition-colors cursor-pointer shadow-xs shrink-0 flex items-center justify-center"
              title="Back to Sub Administrators List"
            >
              <FiArrowLeft className="w-5 h-5 text-slate-900" />
            </button>
            <div className="space-y-0.5 min-w-0">
              <h1 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight">
                {isEdit ? "Edit Sub Administrator" : "Add New Sub Administrator"}
              </h1>
              <p className="text-xs sm:text-sm font-bold text-slate-700">
                {isEdit
                  ? "Update administrative credentials and granular module permission matrix."
                  : "Provision internal sub administrator credentials and assign Role-Based Access Control (RBAC)."}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2.5 self-end sm:self-center shrink-0">
            <button
              type="button"
              onClick={() => navigate(MODULE_CONFIG.routeBase)}
              className="px-4 h-10 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 border border-slate-300 text-xs font-bold transition-all cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting || loading}
              className="inline-flex items-center gap-1.5 px-5 h-10 rounded-xl bg-[#115B9D] hover:bg-[#0e4b82] text-white text-xs font-bold transition-all shadow-md shadow-[#115B9D]/25 cursor-pointer disabled:opacity-60 active:scale-[0.98]"
            >
              <FiSave className="w-4 h-4" />
              <span>{isEdit ? "Update Sub Administrator" : "Create Sub Administrator"}</span>
            </button>
          </div>
        </div>

        {/* ======================================================== */}
        {/* 2. FORM BODY (IDENTITY CREDENTIALS & PERMISSIONS MATRIX) */}
        {/* ======================================================== */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* SECTION 1: Sub Administrator Account Identity & Credentials */}
          <div className="bg-white rounded-2xl p-5 sm:p-6 border border-slate-300 shadow-[0_2px_10px_rgba(0,0,0,0.04)] space-y-4">
            <div className="flex items-center gap-2.5 border-b border-slate-200 pb-3">
              <div className="w-7 h-7 rounded-lg bg-indigo-50 text-indigo-800 border border-indigo-200 flex items-center justify-center font-bold">
                <FiUser className="w-4 h-4" />
              </div>
              <h3 className="text-sm font-black text-slate-900 uppercase tracking-wide">
                Sub Administrator Identity & Credentials
              </h3>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <FormControl
                label="First Name"
                required
                value={formData.firstName}
                onChange={(val) => handleInputChange("firstName", val)}
                placeholder="e.g. Alexander"
              />

              <FormControl
                label="Last Name"
                required
                value={formData.lastName}
                onChange={(val) => handleInputChange("lastName", val)}
                placeholder="e.g. Operations"
              />

              <FormControl
                label="Email Address"
                type="email"
                required
                value={formData.email}
                onChange={(val) => handleInputChange("email", val)}
                placeholder="alex.admin@yopmail.com"
              />

              {/* Password with Generate Password Action (Hidden in Edit Mode) */}
              {!isEdit && (
                <div className="sm:col-span-2 lg:col-span-3">
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-semibold text-slate-800">
                        Login Password <span className="text-red-500">*</span>
                      </label>
                      <button
                        type="button"
                        onClick={generateSecurePassword}
                        className="inline-flex items-center gap-1.5 text-xs font-bold text-[#115B9D] hover:text-[#0e4b82] transition-colors cursor-pointer px-2 py-0.5 rounded-lg hover:bg-slate-100"
                      >
                        <FiRefreshCw className="w-3.5 h-3.5" />
                        <span>{passwordCopied ? "Password Copied!" : "Generate Password"}</span>
                      </button>
                    </div>

                    <FormControl
                      type="password"
                      showEye={true}
                      value={formData.password}
                      onChange={(val) => handleInputChange("password", val)}
                      placeholder="Enter password or click Generate Password above..."
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* SECTION 2: Role-Based Access Control (RBAC) Permissions Matrix */}
          <div className="bg-white rounded-2xl p-5 sm:p-6 border border-slate-300 shadow-[0_2px_10px_rgba(0,0,0,0.04)] space-y-5">
            {/* Permissions Header & Presets Bar */}
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 border-b border-slate-200 pb-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-xl bg-purple-50 text-purple-800 border border-purple-200 flex items-center justify-center font-bold">
                  <FiShield className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="text-sm font-black text-slate-900 uppercase tracking-wide">
                    Module Access & Granular Permissions Matrix
                  </h3>
                  <p className="text-xs font-bold text-slate-600">
                    Granted:{" "}
                    <span className="text-[#115B9D] font-black">
                      {granted} of {total} Actions Active
                    </span>
                  </p>
                </div>
              </div>

              {/* Quick Presets Buttons */}
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={handleGrantAll}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-purple-50 hover:bg-purple-100 text-purple-900 border border-purple-300 text-xs font-bold transition-all cursor-pointer shadow-2xs"
                >
                  <FiCheckSquare className="w-3.5 h-3.5 text-purple-700" />
                  <span>Select All</span>
                </button>

                <button
                  type="button"
                  onClick={handleViewOnlyPreset}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-blue-50 hover:bg-blue-100 text-blue-900 border border-blue-300 text-xs font-bold transition-all cursor-pointer shadow-2xs"
                >
                  <FiLayers className="w-3.5 h-3.5 text-blue-700" />
                  <span>View Only</span>
                </button>

                <button
                  type="button"
                  onClick={handleRevokeAll}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-slate-50 hover:bg-slate-100 text-slate-800 border border-slate-300 text-xs font-bold transition-all cursor-pointer shadow-2xs"
                >
                  <FiSquare className="w-3.5 h-3.5 text-slate-500" />
                  <span>Clear All</span>
                </button>
              </div>
            </div>

            {/* 8 Module Cards Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5">
              {MODULE_PERMISSIONS_SCHEMA.map((mod) => {
                const currentModule = formData.permissions?.[mod.key] || {};
                const allActive = mod.actions.every((act) => Boolean(currentModule[act.key]));
                const someActive = mod.actions.some((act) => Boolean(currentModule[act.key]));

                return (
                  <div
                    key={mod.key}
                    className={`rounded-2xl p-4 sm:p-5 border transition-all ${
                      someActive
                        ? "bg-slate-50/70 border-slate-300 shadow-xs"
                        : "bg-white border-slate-200"
                    }`}
                  >
                    {/* Module Header */}
                    <div className="flex items-start justify-between gap-3 border-b border-slate-200 pb-3">
                      <div>
                        <h4 className="text-xs font-black text-slate-900 uppercase tracking-wide">
                          {mod.label}
                        </h4>
                        <p className="text-[11px] font-medium text-slate-600 mt-0.5">
                          {mod.description}
                        </p>
                      </div>

                      <button
                        type="button"
                        onClick={() => handleToggleEntireModule(mod.key, mod.actions)}
                        className={`text-[11px] font-bold px-2.5 py-1 rounded-lg border transition-colors shrink-0 cursor-pointer ${
                          allActive
                            ? "bg-emerald-100 text-emerald-900 border-emerald-300"
                            : "bg-white text-slate-700 border-slate-300 hover:bg-slate-100"
                        }`}
                      >
                        {allActive ? "Disable All" : "Enable All"}
                      </button>
                    </div>

                    {/* Actions Checkboxes */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 pt-3.5">
                      {mod.actions.map((act) => {
                        const isChecked = Boolean(currentModule[act.key]);
                        return (
                          <label
                            key={act.key}
                            onClick={() => handleToggleAction(mod.key, act.key)}
                            className={`flex items-center gap-2.5 p-2 rounded-xl border text-xs font-bold cursor-pointer transition-all select-none ${
                              isChecked
                                ? "bg-white text-slate-900 border-[#115B9D] shadow-2xs"
                                : "bg-slate-50 text-slate-600 border-slate-200 hover:border-slate-300"
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => {}} // Handled by label click
                              className="w-4 h-4 rounded text-[#115B9D] focus:ring-0 border-slate-400 cursor-pointer"
                            />
                            <span className="truncate">{act.label}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Bottom Save Action */}
          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={() => navigate(MODULE_CONFIG.routeBase)}
              className="px-5 h-11 rounded-xl bg-white hover:bg-slate-100 text-slate-800 border border-slate-300 text-xs font-bold transition-all cursor-pointer shadow-xs"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || loading}
              className="inline-flex items-center gap-2 px-6 h-11 rounded-xl bg-[#115B9D] hover:bg-[#0e4b82] text-white text-xs font-bold transition-all shadow-md shadow-[#115B9D]/25 cursor-pointer disabled:opacity-60 active:scale-[0.98]"
            >
              <FiSave className="w-4 h-4" />
              <span>{isEdit ? "Save Sub Administrator" : "Create Sub Administrator"}</span>
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
